import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Minus, Sparkles } from 'lucide-react';
import PricingCard from '../components/features/PricingCard';
import BottomCTA from '../components/features/BottomCTA';
import { useToast } from '../components/ui/Toast';

// ─── Plans ──────────────────────────────────────────────────────────────────

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for testing and light use — no credit card needed.',
    features: [
      '5 images per day',
      'Small watermark on output',
      'JPG / PNG output',
    ],
    ctaLabel: 'Get Started Free',
    ctaVariant: 'secondary' as const,
  },
  {
    name: 'Pro',
    price: '$14.99',
    period: '/month',
    description: 'For photographers and sellers processing 200+ photos per month.',
    features: [
      '200 images per batch',
      'Unlimited monthly batches',
      'No watermark',
      'Priority queue',
      'Commercial usage rights',
      'Cancel anytime',
    ],
    badge: 'Most Popular',
    highlighted: true,
    ctaLabel: 'Subscribe to Pro',
    ctaVariant: 'primary' as const,
    plan: 'pro' as const,
  },
  {
    name: 'Lifetime',
    price: '$299',
    period: 'one-time',
    description: 'Cap costs forever — limited to 200 early adopters.',
    features: [
      '1,000 images per batch',
      'Unlimited batches forever',
      'No monthly fees',
      'No watermark',
      'Priority queue',
      'Commercial usage rights',
    ],
    limitations: [
      'Fair Use: ~1,000 images/mo avg.',
    ],
    badge: 'Limited',
    ctaLabel: 'Claim Lifetime Offer',
    ctaVariant: 'secondary' as const,
    plan: 'lifetime' as const,
  },
];

// ─── Comparison Data ────────────────────────────────────────────────────────

const comparisonGroups = [
  {
    label: 'Upload & Processing',
    rows: [
      { feature: 'Daily images', free: '5/day', pro: 'Unlimited', lifetime: 'Unlimited' },
      { feature: 'Batch size', free: '200 images', pro: '200 images', lifetime: '1,000 images' },
      { feature: 'Max resolution', free: '4K', pro: '8K', lifetime: '8K' },
      { feature: 'Watermark', free: 'Small logo', pro: '✓ None', lifetime: '✓ None' },
    ],
  },
  {
    label: 'Output & Quality',
    rows: [
      { feature: 'Output formats', free: 'PNG, JPG', pro: 'PNG, JPG, WEBP', lifetime: 'PNG, JPG, WEBP' },
      { feature: 'RAW support', free: 'No', pro: '✓ YES', lifetime: '✓ YES' },
      { feature: 'AI denoising', free: 'No', pro: '✓ YES', lifetime: '✓ YES' },
    ],
  },
  {
    label: 'Business & Support',
    rows: [
      { feature: 'Commercial use', free: 'No', pro: '✓ YES', lifetime: '✓ YES' },
      { feature: 'Priority support', free: 'No', pro: '✓ YES', lifetime: '✓ YES' },
      { feature: 'API access', free: 'No', pro: 'Coming soon', lifetime: 'Coming soon' },
    ],
  },
];

// ─── FAQ ────────────────────────────────────────────────────────────────────

const faqs = [
  {
    q: 'What file formats do you support?',
    a: 'JPG, PNG, WEBP, and RAW formats including CR2, NEF, and ARW. Output is PNG (transparent) or JPG / WEBP (white background).',
  },
  {
    q: 'How does the free tier work?',
    a: 'Free gives you 5 images per day with a small watermark on output. No credit card required. Quota resets every 24 hours.',
  },
  {
    q: "What's the difference between Pro and Lifetime?",
    a: 'Pro is a monthly subscription at $14.99/mo (or $119/year) with 200 images per batch and unlimited monthly batches. Lifetime is a one-time $299 purchase with 1,000 images per batch, unlimited batches, and no recurring fees — limited to the first 200 buyers.',
  },
  {
    q: 'Can I use RSP for client work?',
    a: 'Yes. All paid plans include commercial usage rights. Your outputs are yours to use in client deliverables, e-commerce listings, or print materials.',
  },
  {
    q: 'What happens to my images after processing?',
    a: 'Uploaded files are automatically deleted from our servers 24 hours after processing. We never store or share your images.',
  },
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes. Upgrade from Free to Pro or Lifetime instantly. Downgrade from Pro to Free at the end of your billing cycle. Lifetime purchases are permanent.',
  },
];

// ─── Scroll Reveal Hook ────────────────────────────────────────────────────

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

// ─── Accordion ──────────────────────────────────────────────────────────────

function AccordionItem({ q, a, open, onToggle }: {
  q: string; a: string; open: boolean; onToggle: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="border-b border-border-subtle last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left gap-4 group"
      >
        <span className="text-sm font-medium text-text-primary group-hover:text-brand transition-colors">
          {q}
        </span>
        <ChevronDown
          size={16}
          className={[
            'flex-shrink-0 text-text-muted transition-transform duration-300 ease-out',
            open ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{
          maxHeight: open ? contentRef.current?.scrollHeight ?? 200 : 0,
          opacity: open ? 1 : 0,
        }}
      >
        <p className="text-sm text-text-secondary leading-relaxed pb-5">{a}</p>
      </div>
    </div>
  );
}

// ─── Page Component ─────────────────────────────────────────────────────────

export default function PricingPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { addToast } = useToast();

  // Scroll reveal for pricing cards
  const cardReveal = useScrollReveal(0.1);
  const comparisonReveal = useScrollReveal(0.1);
  const faqReveal = useScrollReveal(0.1);

  const handleCheckout = async (plan: string) => {
    if (plan === 'free') return;

    setLoading(plan);

    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? 'https://rsp-workers.w315194731.workers.dev';
      const response = await fetch(`${apiUrl}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(err.message || 'Failed to create checkout');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      addToast({
        variant: 'error',
        title: 'Checkout failed',
        message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      });
      setLoading(null);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* ─── Hero Section ──────────────────────────────────── */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand/8 rounded-full blur-[120px]" />
          <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] bg-purple-500/6 rounded-full blur-[100px]" />
        </div>

        <div className="w-full max-w-6xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand/10 border border-brand/20 text-brand text-xs font-medium mb-6">
            <Sparkles size={14} />
            Simple pricing, no surprises
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary leading-tight mb-5 tracking-tight">
            Transparent Pricing for
            <br />
            <span className="bg-gradient-to-r from-brand to-purple-400 bg-clip-text text-transparent">
              Professional Results
            </span>
          </h1>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            No per-image fees. No hidden charges. Just powerful AI background removal
            at a flat rate that works for your business.
          </p>
        </div>
      </section>

      {/* ─── Pricing Cards ─────────────────────────────────── */}
      <section className="pb-16 md:pb-24">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div
            ref={cardReveal.ref}
            className={[
              'grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start',
              'transition-all duration-700 ease-out',
              cardReveal.visible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-8 opacity-0',
            ].join(' ')}
          >
            {plans.map((plan, i) => (
              <div
                key={plan.name}
                className="transition-all duration-500 ease-out"
                style={{
                  transitionDelay: `${i * 150}ms`,
                  opacity: cardReveal.visible ? 1 : 0,
                  transform: cardReveal.visible ? 'translateY(0)' : 'translateY(24px)',
                }}
              >
                <PricingCard
                  {...plan}
                  onCheckout={plan.plan ? () => handleCheckout(plan.plan!) : undefined}
                  disabled={loading !== null && loading !== plan.plan}
                  loading={loading === plan.plan}
                />
              </div>
            ))}
          </div>

          {/* Trust note */}
          <p className="text-center text-xs text-text-muted mt-8">
            🔒 Secure checkout powered by Creem. Your payment info is never stored on our servers.
          </p>
        </div>
      </section>

      {/* ─── Feature Comparison ────────────────────────────── */}
      <section className="py-16 md:py-24 bg-bg-elevated">
        <div className="w-full max-w-5xl mx-auto px-6">
          <div
            ref={comparisonReveal.ref}
            className={[
              'transition-all duration-700 ease-out',
              comparisonReveal.visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
            ].join(' ')}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text-primary mb-3">
              Compare Plans Side by Side
            </h2>
            <p className="text-sm text-text-secondary text-center mb-12 max-w-xl mx-auto">
              All the details you need to choose the right plan for your workflow.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-left py-4 pr-6 text-xs font-semibold uppercase tracking-wider text-text-muted w-[200px]">
                      Feature
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-text-primary">
                      Free
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-brand">
                      Pro
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-text-primary">
                      Lifetime
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonGroups.map((group) => (
                    <>
                      <tr key={group.label}>
                        <td
                          colSpan={4}
                          className="pt-6 pb-2 text-xs font-semibold uppercase tracking-wider text-text-muted"
                        >
                          {group.label}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr
                          key={row.feature}
                          className="border-b border-border-subtle/50 hover:bg-bg-overlay/30 transition-colors"
                        >
                          <td className="py-3.5 pr-6 text-sm text-text-primary font-medium">
                            {row.feature}
                          </td>
                          {['free', 'pro', 'lifetime'].map((tier) => {
                            const val = row[tier as keyof typeof row];
                            const isCheck = val.includes('✓');
                            return (
                              <td
                                key={tier}
                                className="py-3.5 px-4 text-center"
                              >
                                {isCheck ? (
                                  <span className="inline-flex items-center gap-1.5 text-sm text-semantic-success font-medium">
                                    <Check size={13} strokeWidth={3} />
                                    {val.replace('✓ ', '')}
                                  </span>
                                ) : val === 'No' || val?.startsWith('No') ? (
                                  <span className="text-sm text-text-disabled">
                                    <Minus size={14} className="inline" strokeWidth={3} />
                                  </span>
                                ) : (
                                  <span className="text-sm text-text-secondary">
                                    {val}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────── */}
      <section className="py-16 md:py-24">
        <div className="w-full max-w-3xl mx-auto px-6">
          <div
            ref={faqReveal.ref}
            className={[
              'transition-all duration-700 ease-out',
              faqReveal.visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
            ].join(' ')}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text-primary mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-sm text-text-secondary text-center mb-10 max-w-xl mx-auto">
              Everything you need to know about RSP pricing and usage.
            </p>

            <div className="bg-bg-surface rounded-2xl border border-border-subtle px-6 md:px-8">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  q={faq.q}
                  a={faq.a}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>

            <p className="text-center text-sm text-text-muted mt-8">
              Still have questions?{' '}
              <a
                href="mailto:support@rsp.app"
                className="text-brand hover:text-brand-hover underline underline-offset-2 transition-colors"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ────────────────────────────────────── */}
      <BottomCTA
        headline="Ready to remove backgrounds at scale?"
        subheadline="Start for free — no credit card required. Upgrade anytime."
      />
    </div>
  );
}
